import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Col, Row, Select, Space,
} from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import TableProjectPreload from './TableProjectPreload';
import ProjectsService from '../../../../projects/state/projects.service';
import { getScheduleOfValueTemplates } from '../../../../projects/state/projects.actions';

const DEFAULT_SECTION_OPTIONS = [
  { label: 'Default - Base Contract', value: 'base' },
  { label: 'Default - Sub-Contracts', value: 'subcontracts' },
  { label: 'Default - Changes', value: 'changes' },
  { label: 'Default - Non-holdback Items', value: 'nonHoldback' },
];

export default function TableScheduleOfValuesPreload({
  configProps,
  updateConfig,
  divisions,
  sections = [],
}) {
  const dispatch = useDispatch();
  const scheduleOfValueTemplates = useSelector((state) => state.projects.scheduleOfValueTemplates);
  const [templateSections, setTemplateSections] = useState([]);
  const [loadingTemplateSections, setLoadingTemplateSections] = useState(false);

  const {
    preloadSovTemplateId,
  } = configProps ?? {};

  useEffect(() => {
    dispatch(getScheduleOfValueTemplates());
  }, []);

  useEffect(() => {
    if (!preloadSovTemplateId) {
      setTemplateSections([]);
      setLoadingTemplateSections(false);
      return;
    }

    let cancelled = false;
    const loadTemplateSections = async () => {
      setLoadingTemplateSections(true);
      const res = await ProjectsService.getScheduleOfValueSections({
        templateId: preloadSovTemplateId,
      });
      if (!cancelled) {
        setTemplateSections(res?.data ?? []);
        setLoadingTemplateSections(false);
      }
    };
    loadTemplateSections();
    return () => { cancelled = true; };
  }, [preloadSovTemplateId]);

  const templateOptions = useMemo(() => {
    const divSet = new Set(divisions);
    const customTemplates = scheduleOfValueTemplates
      .filter((template) => 
        divSet.has(template.divisionId))
      .map((template) => ({ label: template.name, value: template.id }));
    return [
      { label: 'Default', value: null },
      ...customTemplates,
    ];
  }, [scheduleOfValueTemplates, divisions]);

  const sectionOptions = useMemo(() => {
    const sortedTemplateSections = [...templateSections].sort((a, b) => (
      (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
    ));
    return DEFAULT_SECTION_OPTIONS.concat(
      sortedTemplateSections.map((section) => ({
        label: `Template - ${section.name}`,
        value: section.id,
      })),
    );
  }, [templateSections]);

  const onTemplateChange = useCallback((value) => {
    updateConfig({
      preloadSovTemplateId: value,
      preloadSovSectionId: null,
    });
  }, [updateConfig]);

  const onSectionChange = useCallback((value) => {
    updateConfig({ preloadSovSectionId: value });
  }, [updateConfig]);

  return (
    <Space direction="vertical">
      <TableProjectPreload
        configProps={configProps}
        updateConfig={updateConfig}
        divisions={divisions}
        sections={sections}
        hideTitle
      />
      <Row align="middle" gutter={10} style={{ marginLeft: 0 }}>
        <Col>
          Contract Template
        </Col>
        <Col>
          <Select
            style={{ width: 500 }}
            options={templateOptions}
            optionFilterProp="label"
            allowClear
            showSearch
            placeholder="Select a Contract Template"
            value={preloadSovTemplateId}
            onChange={onTemplateChange}
          />
        </Col>
      </Row>
      <Row align="middle" gutter={10} style={{ marginLeft: 0 }}>
        <Col>
          SOV Section
        </Col>
        <Col>
          <Select
            style={{ width: 500 }}
            options={sectionOptions}
            optionFilterProp="label"
            allowClear
            showSearch
            loading={loadingTemplateSections}
            disabled={preloadSovTemplateId === undefined}
            placeholder="Select an SOV Section"
            value={configProps.preloadSovSectionId}
            onChange={onSectionChange}
          />
        </Col>
      </Row>
    </Space>
  );
}

TableScheduleOfValuesPreload.propTypes = {
  configProps: PropTypes.object.isRequired,
  updateConfig: PropTypes.func.isRequired,
  divisions: PropTypes.arrayOf(PropTypes.string).isRequired,
  sections: PropTypes.array,
};

TableScheduleOfValuesPreload.defaultProps = {
  sections: [],
};
